function decimalToHex(d: number) {
  return d.toString(16);
} // convert a decimal value to hex
function hexToDecimal(h: string) {
  return parseInt(h, 16);
} // co

export const mixColor = function (colorA: string, colorB: string, weight = 50) {
  let color = "#";

  colorA = colorA.replace("#", "");
  colorB = colorB.replace("#", "");

  if (colorA.length < 6) return "#000000";
  if (colorB.length < 6) return "#000000";

  for (let i = 0; i <= 5; i += 2) {
    const v1 = hexToDecimal(colorA.substr(i, 2));
    const v2 = hexToDecimal(colorB.substr(i, 2));
    // combine the current pairs from each source color, according to the specified weight
    let val = decimalToHex(Math.floor(v2 + (v1 - v2) * (weight / 100.0)));

    while (val.length < 2) {
      val = "0" + val;
    }

    color += val;
  }

  return color;
};

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

const HEX_RE = /^#?([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const OPAQUE_HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

function parseHex(hex: string): Rgb | undefined {
  // Shape-checked before parsing: `parseInt("0g", 16)` is 0, not NaN, so
  // per-channel parsing alone would read "#0g0g0g" as black.
  const match = HEX_RE.exec(hex.trim());
  if (!match) return undefined;
  const cleanHex = match[1];

  let r: number, g: number, b: number;

  if (cleanHex.length === 3 || cleanHex.length === 4) {
    // 3 or 4 digit hex -> double each
    r = parseInt(cleanHex[0] + cleanHex[0], 16);
    g = parseInt(cleanHex[1] + cleanHex[1], 16);
    b = parseInt(cleanHex[2] + cleanHex[2], 16);
  } else if (cleanHex.length === 6 || cleanHex.length === 8) {
    // 6 or 8 digit hex -> take pairs
    r = parseInt(cleanHex.substring(0, 2), 16);
    g = parseInt(cleanHex.substring(2, 4), 16);
    b = parseInt(cleanHex.substring(4, 6), 16);
  } else {
    // Unreachable for a `HEX_RE` match; kept so the branches stay exhaustive.
    return undefined;
  }

  return { r, g, b };
}

/**
 * Contrast only means something over a known backdrop, so the alpha-bearing
 * forms `parseHex` accepts — dropping the alpha as it goes — are refused here
 * rather than measured as if they were opaque.
 */
function parseOpaqueHex(hex: string): Rgb | undefined {
  return OPAQUE_HEX_RE.test(hex.trim()) ? parseHex(hex) : undefined;
}

export function hexToRgb(hex?: string): string | undefined {
  if (!hex) return undefined;
  const rgb = parseHex(hex);
  if (!rgb) return undefined;
  return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
}

export function isColorLight(hex?: string): boolean {
  if (!hex) return false;

  const rgb = parseHex(hex);
  if (!rgb) return false;

  // Calculate luminance
  // Normalized to 0-1
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;

  return lightness > 0.75;
}

// Picks a near-white or near-black text color that contrasts with the given
// background hex. Uses perceptual luminance (ITU-R BT.601) rather than HSL
// lightness, so colors like pure yellow correctly read as "light" and get
// dark text. Returns `undefined` only when no hex is supplied, so the
// caller can leave the CSS default in place. Invalid hex strings fall back
// to the "use light text" branch.
export function getContrastingTextColor(hex?: string): string | undefined {
  if (!hex) return undefined;
  const rgb = parseHex(hex);
  if (!rgb) return "#FFFFFF";
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.67 ? "#141516" : "#FFFFFF";
}

function toSixDigitHex({ r, g, b }: Rgb) {
  return `#${[r, g, b]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")}`;
}

/**
 * WCAG 2.1 relative luminance: sRGB channels gamma-expanded, then weighted.
 * Not interchangeable with the BT.601 luminance `getContrastingTextColor` and
 * page-blocks' own `isHexLight` use — those answer "is this light?" against a
 * tuned threshold, this one feeds ratio math.
 */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const s = value / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * WCAG contrast ratio between two colors, from 1 (identical) to 21 (black on
 * white). `undefined` when either color can't be parsed, so a caller can't
 * mistake a failure to measure for a passing ratio.
 */
export function contrastRatio(
  hexA?: string,
  hexB?: string
): number | undefined {
  const a = hexA ? parseOpaqueHex(hexA) : undefined;
  const b = hexB ? parseOpaqueHex(hexB) : undefined;
  if (!a || !b) return undefined;

  return ratioOfLuminances(relativeLuminance(a), relativeLuminance(b));
}

function ratioOfLuminances(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** WCAG AA for body text. */
const READABLE_MIN_RATIO = 4.5;

/**
 * Nudges `hex` toward black or white — whichever leads away from the
 * background — until it clears `minRatio` against every background given,
 * keeping as much of the original color as the ratio allows: a color used as
 * text stays recognisably itself instead of collapsing straight to black or
 * white.
 *
 * Several backgrounds, because one colour often has to read on more than one
 * surface — a link sits on a card in one block and on the page itself in the
 * next. They are ordered by priority: where no mix can satisfy them all (a
 * light surface over a dark page), the walk ends on whichever end stop reads
 * on the first.
 */
export function ensureReadableColor(
  hex?: string,
  background?: string | Array<string | undefined>,
  minRatio: number = READABLE_MIN_RATIO
): string | undefined {
  if (!hex) return undefined;
  const rgb = parseOpaqueHex(hex);

  const luminances = (Array.isArray(background) ? background : [background])
    .map((entry) => entry && parseOpaqueHex(entry))
    .filter((entry): entry is Rgb => !!entry)
    .map(relativeLuminance);

  if (!rgb || !luminances.length) return hex;

  const color = toSixDigitHex(rgb);
  // 0.179 is where black and white text swap places under WCAG, so a
  // background above it is one to darken away from. The first background wins
  // the direction: it is the one the fallback has to read on.
  const target = luminances[0] > 0.179 ? "#000000" : "#FFFFFF";

  // A walk rather than a binary search: 5% steps are finer than the eye reads
  // as a different color, and the first pass keeps the most saturated version
  // that clears the bar. `keep = 100` is the color untouched; `keep = 0` is
  // `target` itself — always readable on the first background, hence the loop
  // stopping above it.
  for (let keep = 100; keep > 0; keep -= 5) {
    const candidate = mixColor(color, target, keep);
    const candidateRgb = parseOpaqueHex(candidate);
    if (!candidateRgb) continue;

    const candidateLuminance = relativeLuminance(candidateRgb);
    if (
      luminances.every(
        (luminance) =>
          ratioOfLuminances(candidateLuminance, luminance) >= minRatio
      )
    )
      return candidate;
  }

  return target;
}
